import type { FC } from 'react'

import { EmployeesShowPageDocument, ShowPageFieldsDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import TaskElement from 'modules/tasks/components/TaskElement'
import { TaskIdentifier } from 'modules/tasks/lib/task'

const Component: FC = () => {
  const { navigation: navigation } = useNavigation()

  const { id, taskId } = useStrictParams<'id' | 'taskId'>()
  const back = navigation.employees.id.tasks.fullPath({ id })

  // Query 1: task data — fires immediately
  const { data, loading } = useQuery(EmployeesShowPageDocument, {
    variables: {
      taskId: String(taskId),
    },
  })

  const affecteeEmployeeId =
    data?.tasks.tasksConnection.edges[0]?.node?.affecteesConnection.edges[0]?.node?.employee?.id

  // Query 2a: fields scoped to the assignee (URL id) — fires immediately in parallel with query 1.
  // Covers the common case: assignee filling fields about themselves (affectee absent or same as assignee).
  const { data: assigneeFieldsData, loading: assigneeFieldsLoading } = useQuery(
    ShowPageFieldsDocument,
    { variables: { relatedEmployeeId: id } }
  )

  // Query 2b: fields scoped to the affectee — conditional waterfall.
  // Only fires when query 1 reveals a different affectee (e.g. manager filling fields for a new hire).
  // Skipped when affectee is absent or matches the assignee, to avoid a redundant duplicate request.
  const needsAffecteeFieldsQuery = !!affecteeEmployeeId && affecteeEmployeeId !== id
  const { data: affecteeFieldsData, loading: affecteeFieldsLoading } = useQuery(
    ShowPageFieldsDocument,
    {
      variables: { relatedEmployeeId: affecteeEmployeeId },
      skip: !needsAffecteeFieldsQuery,
    }
  )

  // Use the affectee-scoped result when the affectee is a different employee;
  // otherwise the assignee-scoped result is already correct.
  const activeFieldsData = needsAffecteeFieldsQuery ? affecteeFieldsData : assigneeFieldsData
  const fieldsLoading = needsAffecteeFieldsQuery ? affecteeFieldsLoading : assigneeFieldsLoading

  const customResourceFieldsData =
    compact(
      activeFieldsData?.customFields.resourceFieldsConnection.edges.map((edge) => edge.node)
    ) ?? []

  const customValuesData =
    data?.tasks.tasksConnection.edges[0]?.node?.affecteesConnection.edges[0]?.node?.employee?.valuesConnection.edges.map(
      (edge) => edge.node
    ) ?? []

  const canEmployeeManageAnyEmployeeFolder =
    data?.documents._policies.employeeManageAnyEmployeeFolder.can ?? false

  // Show single task from employee page
  return (
    <TaskElement
      taskId={String(taskId)}
      back={back}
      identifier={TaskIdentifier.Single}
      canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
      loading={loading || fieldsLoading}
      customResourceFieldsData={customResourceFieldsData}
      customValuesData={customValuesData}
    />
  )
}

export default Component
